/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { ButtonBase, DndDraggable, DndDragHandle, Icon } from "../../core";
import { useFieldListItemContext } from "../../../contexts";

import "./FieldListItemHeader.scss";

export interface IFieldListItemHeaderProps {
	children?: React.ReactNode;
	className?: string;
	secondaryAction?: React.ReactNode;
	onClick?: React.MouseEventHandler;
	selected?: boolean;
	endAdornment?: React.ReactNode;
}

export const FieldListItemHeader: React.FC<IFieldListItemHeaderProps> = ({
	children,
	className,
	secondaryAction,
	onClick,
	selected,
	endAdornment
}) => {
	const fieldListItemCtx = useFieldListItemContext();
	const baseClass = BEM("edt-field-list-item-header", null, {
		selected
	});

	const draggable = fieldListItemCtx?.draggable;

	const handleClick = useCallback<React.MouseEventHandler>(
		(event) => {
			if (isFunction(onClick)) {
				onClick(event);
			}

			if (isFunction(fieldListItemCtx?.onExpandChange)) {
				fieldListItemCtx?.onExpandChange(event);
			}
		},
		[ onClick, fieldListItemCtx, fieldListItemCtx?.onExpandChange ]
	);

	if (className) {
		baseClass.add(className);
	}

	if (draggable) {
		return (
			<DndDraggable
				draggableId={fieldListItemCtx?.id}
				index={fieldListItemCtx?.index}
				className={baseClass.toClassName()}
			>
				<DndDragHandle className={baseClass.element("handle").toClassName()}>
					<Icon className={baseClass.element("handle-icon").toClassName()} name="mdi/drag" />
				</DndDragHandle>

				<ButtonBase
					className={baseClass.element("button").toClassName()}
					disabled={fieldListItemCtx?.disabled}
					onClick={handleClick}
				>
					{children}
				</ButtonBase>

				{endAdornment}

				{secondaryAction && (
					<>
						<div className={baseClass.element("separator").toClassName()} />
						{secondaryAction}
					</>
				)}
			</DndDraggable>
		);
	}

	return (
		<div className={baseClass.toClassName()}>
			<ButtonBase
				className={baseClass.element("button").toClassName()}
				disabled={fieldListItemCtx?.disabled}
				onClick={handleClick}
			>
				{children}
			</ButtonBase>

			{endAdornment}

			{secondaryAction && (
				<>
					<div className={baseClass.element("separator").toClassName()} />
					{secondaryAction}
				</>
			)}
		</div>
	);
};
